import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import "./reviews.css";
import { listReviews, useInboxLink, type ReviewSummary } from "./api";
import { ReviewRoom } from "./ReviewRoom";
import { TYPE_LABEL, ThemeIcon, ago, useHomeScreen, useReviewTheme } from "./shell";
import { docTag, topLevel } from "./content";

// RV-4 — "My reviews" (/my/<code>): one private link per account, made for a phone's home screen.
// It lists the account's reviews (new ones show up on their own) and opens any of them, full
// screen, with no sign-in. Keep the link private: it opens every review of the account.
// A review opens inside this same page (the address never changes): an iPhone home-screen app
// shows Safari's bars as soon as the address moves away from the one that was saved.

export function MyReviews() {
  const { code = "" } = useParams();
  const [theme, toggleTheme] = useReviewTheme();
  const [reviews, setReviews] = useState<ReviewSummary[] | null>(null);
  const [error, setError] = useState("");
  // the rooms opened, newest last: a linked document opens on top of its question's room, and
  // Back returns to that room before the list
  const [stack, setStack] = useState<string[]>([]);
  const openId = stack[stack.length - 1] || null;
  const setOpenId = (id: string | null) => setStack(id ? [id] : []);
  useHomeScreen("Reviews");
  const load = () => { useInboxLink(code); listReviews().then(setReviews).catch((e) => { setError(e instanceof Error ? e.message : "This link isn't working."); setReviews([]); }); };
  useEffect(() => { load(); return () => useInboxLink(null); }, [code]);
  if (openId) {
    useInboxLink(code, openId);
    const partOf = stack.length === 1 ? (reviews || []).find((r) => r.id === openId)?.partOf || null : null;
    return <ReviewRoom key={openId} shared partOf={partOf} onOpenParent={(pid) => setStack([pid])} onBack={() => { if (stack.length > 1) setStack(stack.slice(0, -1)); else { setOpenId(null); load(); } }} onOpenDoc={(d) => setStack([...stack, d.id])} />;
  }
  // a document room linked from an open review sits under that review, not as its own row
  const top = topLevel(reviews || []);
  const open = top.filter((r) => r.status === "open"), done = top.filter((r) => r.status === "done");
  const row = (r: ReviewSummary) => {
    const pct = r.items ? Math.round((r.decided / r.items) * 100) : 0;
    const tag = docTag(r);
    return (
      <div className="lr-row" key={r.id}>
        <button className="main" onClick={() => setOpenId(r.id)}>
          <span className="t">{r.title}</span>
          <span className="s">{[r.client, `came in ${ago(r.receivedAt)}`].filter(Boolean).join(" · ")}</span>
          <span className="s" style={{ display: "flex", gap: 8, alignItems: "center" }}><span className="lr-type">{TYPE_LABEL[r.type] || r.type}</span><span>{r.decided} of {r.items} decided</span></span>
          {tag && <span className="s lr-doctag">{tag}</span>}
          <span className="bar"><i style={{ width: `${pct}%` }} /></span>
        </button>
      </div>
    );
  };
  return (
    <div className="lr shared" data-theme={theme}>
      <header className="lr-hdr">
        <div className="grow"><h1>Reviews</h1><div className="sub">{reviews === null ? "Loading…" : <><b>{open.length}</b> open</>}</div></div>
        <button className="icon" onClick={toggleTheme} aria-label={theme === "dark" ? "Light mode" : "Dark mode"}><ThemeIcon theme={theme} /></button>
      </header>
      <main className="lr-scroll">
        {error && <div className="lr-error">{error}</div>}
        {reviews !== null && !error && !open.length && <div className="lr-empty"><b>Nothing waiting on you.</b><span>New reviews show up here on their own.</span></div>}
        <div className="lr-list">{open.map(row)}</div>
        {!!done.length && <><div className="lr-donehead">Done</div><div className="lr-list">{done.map(row)}</div></>}
      </main>
    </div>
  );
}

// one review opened from the My reviews list: the room, with a way back to the list
export function MyReviewRoom() {
  const { code = "", id = "" } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [ready, setReady] = useState(false);
  useHomeScreen("Reviews");
  useEffect(() => { useInboxLink(code, id); setReady(true); return () => useInboxLink(null); }, [code, id]);
  return ready ? <ReviewRoom key={id} shared backTo={`/my/${code}`} onOpenDoc={(d) => navigate(`/my/${code}/${encodeURIComponent(d.id)}`, { state: { from: pathname } })} /> : null;
}
